/**
 * Thin fetch wrapper. Same-origin in both dev (via the Vite proxy) and production
 * (Caddy serves the SPA and proxies /api on one host), so the session cookie is
 * sent without any CORS special-casing.
 */

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: unknown,
    /** Present on COMPLIANCE errors, e.g. 'MCL 554.602'. */
    readonly statute?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

interface ApiErrorBody {
  error?: { code?: string; message?: string; details?: unknown; statute?: string };
  // Better Auth's own routes (e.g. /auth/change-password, /auth/reset-password)
  // return a flat {code, message} body instead of nesting under `error` — this
  // is the fallback for that shape, not a second error format of our own.
  code?: string;
  message?: string;
}

export async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`/api${path}`, {
    ...init,
    // The session is an httpOnly cookie. There is no token in localStorage to
    // steal via XSS.
    credentials: 'same-origin',
    headers: {
      // Only when there is a body to describe. Fastify's default JSON parser
      // treats `content-type: application/json` on a bodyless request as
      // malformed input (FST_ERR_CTP_EMPTY_JSON_BODY) rather than "nothing to
      // parse" — a bodyless action (finalize an upload, start a Stripe
      // enrollment) sending this header unconditionally 400s before the
      // route handler ever runs.
      ...(init?.body ? { 'content-type': 'application/json' } : {}),
      ...init?.headers,
    },
  });

  if (!response.ok) {
    let body: ApiErrorBody = {};
    try {
      body = (await response.json()) as ApiErrorBody;
    } catch {
      // A non-JSON error body means the request never reached Fastify — a proxy
      // or TLS problem rather than an application error.
    }
    throw new ApiError(
      response.status,
      body.error?.code ?? body.code ?? 'UNKNOWN',
      body.error?.message ?? body.message ?? response.statusText,
      body.error?.details,
      body.error?.statute,
    );
  }

  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

export interface HealthResponse {
  status: string;
  database?: string;
}

/**
 * Not under /api — Caddy routes /healthz separately so a health probe never
 * depends on the API prefix being right.
 */
export async function getHealth(): Promise<HealthResponse> {
  const response = await fetch('/healthz', { credentials: 'same-origin' });
  if (!response.ok) {
    throw new ApiError(response.status, 'UNAVAILABLE', 'The API is not reachable.');
  }
  return (await response.json()) as HealthResponse;
}
